// src/components/forms/SurveyListDraft.js
import React, { useState } from 'react';
import { CirclePlay , Edit, Archive, Eye, MoreVertical, Layers2} from 'lucide-react';
import { useNavigate } from 'react-router-dom'; 
import { toast } from "sonner"
// Removed shadcn/ui AlertDialog and Button imports

import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi'; // Adjust path if needed
import { useAuth } from '../../hooks/useAuth'; // Adjust path if needed
import Button from './Button';

import API_CONFIG from '../../config/api'
const BASE_URL = API_CONFIG.BASE_URL;

// Expects surveyInfo (matching SurveyInfo Pydantic model) and refreshData function
function SurveyListDraftItem({ surveyInfo, refreshData }) {
	const { authenticatedFetch } = useAuthenticatedApi();
	const { isAuthenticated } = useAuth(); // Assuming useAuth provides isAuthenticated or user object

	const [dropdownOpen, setDropdownOpen] = useState(false);
	const surveyTitleDisplay = surveyInfo?.title || 'Untitled Survey';
	const navigate = useNavigate();

	const handleDeleteClick = async () => {
		// TODO implement archival 
		const confirmed = window.confirm("Are you absolutely sure you want to delete this survey?");

		if (confirmed) {
			console.log(`Attempting to delete survey with ID: ${surveyInfo.survey_id}`);
		} else {
			console.log("Delete action cancelled.");
		}
	};

	const handleActivateClick = async () => {
		const confirmed = window.confirm("🚨​ Once activated, a survey is not editable anymore");
		if (!confirmed) {
			toast("Activation cancelled.")
			console.log("Activation cancelled.");
			return;
		}
		if (!isAuthenticated) { 
			return;
		}

		try {
			const url = BASE_URL + `/draft_surveys/activate-survey/${surveyInfo.survey_id}`
			const result = await authenticatedFetch(url, {}, 'PUT');
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to update the status of the survey to active ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				errorMessage = "🚨​​⁉️" + errorMessage
				console.error("SurveyListDraftItem API Error:", errorMessage, result);
				toast(errorMessage);
				return; // Stop further execution
			}
			const data = await result.json()
			if (data && data.message && data.survey_id) {
				toast(`✅​ ${data.survey_id} activated`);

			} else {
				console.error("Failed to update status:", result);
				toast("Unexpected API response", data);
			}

		} catch (err) {
			const errMessage = "​⁉️​ Error updating status: " + err
			console.error(errMessage);
			toast(errMessage);
		} finally {
			refreshData(); // <--- THIS IS THE KEY STEP!
			closeDropdown();
		}
	}

	const handleEditClick = () => {
		console.log(`Navigating to edit survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/private/evaa-editor/${surveyInfo.survey_id}`);
	};

	const handlePreviewClick = () => {
		console.log(`Navigating to preview survey with ID: ${surveyInfo.survey_id}`);
		navigate(`/private/evaa-survey/preview/${surveyInfo.survey_id}`);
	};

	const handleDuplicateClick = async () => {
		try {
			const url = BASE_URL + `/surveys/duplicate/${surveyInfo.survey_id}`
			const result = await authenticatedFetch(url, {}, 'POST');
			if (!result.ok) {
				// Handle other non-2xx status codes (e.g., 500, 401, 403)
				let errorMessage = `Failed to duplicate survey to draft ${result.status}.`;
				try {
					const errorBody = await result.json();
					if (errorBody && errorBody.detail) {
						errorMessage = errorBody.detail;
					}
				} catch (jsonError) {
					// Ignore if response body isn't JSON
				}
				console.error("SurveyListActiveItem API Error in handleDuplicateClick:", errorMessage, result);
				toast(errorMessage);
				return; // Stop further execution
			}
			const data = await result.json()
			if (data && data.title && data.survey_id) {
				toast(`✅​ survey successfully duplicated`);

			} else {
				console.error("Failed to duplicate survey:", result);
				toast("Unexpected API response", data);
			}

		} catch (err) {
			const errMessage = "​⁉️​ Error while duplicating the survey: " + err
			console.error(errMessage);
			toast(errMessage);
		} finally {
			refreshData(); // <--- THIS IS THE KEY STEP!
			closeDropdown();
		}
	};

	const toggleDropdown = () => {
		setDropdownOpen(!dropdownOpen);
	};

	const closeDropdown = () => {
		setDropdownOpen(false);
	};

	// --- End Placeholder actions ---

	return (
		<div className='border shadow-sm rounded-lg p-4 flex flex-col h-full relative'> {/* Added relative for dropdown positioning */}
			<div className='flex justify-between items-center'> {/* Added items-center for vertical alignment */}
				{/* Original h2 was empty, removing it */}
				<h2 className='text-lg font-semibold text-black flex-grow truncate'>{surveyTitleDisplay}</h2> {/* Use comment field for title */}
				<div className="flex items-center gap-1">
					<span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">Draft</span>
				</div>
			</div>

			{/* Display creation date or survey ID if desired */}
			<h3 className='text-sm text-gray-500 mt-1 mb-4'>
				 ID: {surveyInfo.survey_id} - Created: {new Date(surveyInfo.created_at).toLocaleDateString()}
			</h3>

			<hr className='my-2'></hr> {/* Reduced margin slightly */}

			{/* Action Buttons */}
			<div className='flex justify-between items-center mt-auto'> {/* mt-auto pushes buttons to the bottom */}
				{/* Primary Actions - Most Important */}
				<div className='flex gap-2'>
					<Button
						onClick={handleEditClick}
						variant="primary"
						className="flex gap-2 items-center px-3 py-1.5 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 transition-colors"
					>
						<Edit className='h-4 w-4'/> Edit
					</Button>

					<Button
						onClick={handlePreviewClick}
						variant="secondary"
						className="flex gap-2 items-center px-3 py-1.5 border rounded text-sm text-gray-700 hover:bg-gray-100 transition-colors"
					>
						<Eye className='h-4 w-4'/> Preview
					</Button>
				</div>

				{/* More Actions Dropdown */}
				<div className='relative'>
					<button
						onClick={toggleDropdown}
						className="flex items-center justify-center w-8 h-8 border rounded hover:bg-gray-100 transition-colors"
					>
						<MoreVertical className='h-4 w-4'/>
					</button>

					{dropdownOpen && (
						<>
							{/* Overlay to close dropdown when clicking outside */}
							<div
								className="fixed inset-0 z-10"
								onClick={closeDropdown}
							></div>
							
							{/* Dropdown Menu */}
							<div className="absolute right-0 mt-1 w-48 bg-white border rounded-md shadow-lg z-20">
								<div className="py-1">
									<button
										onClick={() => { handleActivateClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
									>
										<CirclePlay className='h-4 w-4'/> Activate
									</button>
									<hr className="my-1"/>
									<button
										onClick={() => { handleDuplicateClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-red-50 flex items-center gap-2"
									>
										<Layers2 className='h-4 w-4'/> Duplicate to draft
									</button>
									<button
										onClick={() => { handleDeleteClick(); closeDropdown(); }}
										className="w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50 flex items-center gap-2"
									>
										<Archive className='h-4 w-4'/> Archive
									</button>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

export default SurveyListDraftItem;